// Internal helpers shared by more than one sageVoiceApi domain file.
// Not re-exported from index.ts — matches the original file where these were module-private.

import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import type { CallAnalyticsFunnelStep } from './callAnalytics';
import type { VoiceIntegrationRow } from './voiceIntegrations';
import type { VoiceSettingsTeamMember } from './voiceSettings';

/**
 * Lazy-created axios instance with Bearer token for Voice API (used when no context client is passed).
 * Note: Standalone usage requires a valid token which might not be available here easily without context.
 */
export function getVoiceFallbackClient(): AxiosInstance {
  const url = apiConfig.baseUrls.voice;
  return axios.create({
    baseURL: url,
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Helper to resolve path for voice API */
export function voiceRequestUrl(path: string): string {
  const base = apiConfig.baseUrls.voice;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

export function fmtCount(n: unknown): string {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

/** Renders a raw seconds value (e.g. backend's avg_duration=1479.1) as
 * "24 min 39 sec" / "45 sec" / "2 min" instead of a bare, unit-less number —
 * shared by every avg-duration column across the analytics page/exports. */
export function fmtDurationSeconds(n: unknown): string {
  if (n == null || !Number.isFinite(Number(n)) || Number(n) < 0) return '—';
  const totalSeconds = Number(n);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  if (minutes === 0) return `${seconds} sec`;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes} min ${seconds} sec`;
}

/** Like `fmtCount`, but empty string instead of em dash — used where numbers must come only from API data. */
export function fmtAnalyticsCount(n: unknown): string {
  if (n == null || Number.isNaN(Number(n))) return '';
  return Number(n).toLocaleString();
}

/** When funnel steps omit `percent`, derive bar width from the largest count so the overview funnel isn't all zeros. */
export function enrichFunnelPercents(steps: CallAnalyticsFunnelStep[]): CallAnalyticsFunnelStep[] {
  const counts = steps.map((s) => {
    const n = Number(String(s.value).replace(/,/g, ''));
    return Number.isNaN(n) ? 0 : n;
  });
  const max = Math.max(1, ...counts);
  return steps.map((s, i) => ({
    ...s,
    percent: s.percent > 0 ? s.percent : Math.round((counts[i] / max) * 100),
  }));
}

export function mapConnectionToIntegrationRow(c: any): VoiceIntegrationRow {
  const intId = String(c.integration_id ?? '');
  return {
    id: intId || String(c.id),
    name: String(c.name ?? intId ?? 'Integration'),
    desc: String(c.description ?? ''),
    icon: 'solar:plug-circle-bold',
    connected: true,
    category: 'integration',
    fields: [],
    connectionId: String(c.id),
  };
}

export function mapMemberToVoiceTeamMember(m: any): VoiceSettingsTeamMember {
  const name = m.name || m.invited_email || 'Member';
  const email = m.email || m.invited_email || '';
  const initials = (name || email || '?')
    .split(/\s+/)
    .map((p: string) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return {
    id: m.id,
    name,
    email,
    role: m.role || 'Member',
    avatar: initials || '—',
    lastActive: m.status === 'pending' ? 'Invited' : 'Active',
  };
}
